import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ScrollText, Search, ArrowLeft, User as UserIcon } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

/**
 * Per-club audit log surface. Lists every back-office action stamped with
 * the staff member who performed it. Server is the source of truth for
 * scoping (current_club_id) — this page just renders.
 */
const ACTION_GROUPS = [
  { key: "all", label: "All actions", prefix: "" },
  { key: "member", label: "Members", prefix: "member." },
  { key: "order", label: "Orders", prefix: "order." },
  { key: "wishlist", label: "Wishlist", prefix: "wishlist." },
  { key: "invitation", label: "Invitations", prefix: "invitation" },
  { key: "staff", label: "Staff", prefix: "staff." },
  { key: "back_office", label: "Sessions", prefix: "back_office." },
  { key: "membership", label: "Memberships", prefix: "membership." },
];

export default function AdminAuditPage() {
  const [actionFilter, setActionFilter] = useState("all");
  const [actorFilter, setActorFilter] = useState("");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [staffOptions, setStaffOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit: 500 };
      const grp = ACTION_GROUPS.find((g) => g.key === actionFilter);
      if (grp && grp.prefix) params.action = grp.prefix;
      if (actorFilter) params.actor_id = actorFilter;
      const { data } = await api.get("/admin/audit", { params });
      setRows(data || []);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }, [actionFilter, actorFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    // Populate the "Author" filter dropdown from /admin/staff.
    api.get("/admin/staff").then((r) => setStaffOptions(r.data || [])).catch(() => {});
  }, []);

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) =>
      [r.action, r.actor_email, r.actor_name, r.target_id, JSON.stringify(r.meta || {})]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(needle))
    );
  }, [rows, search]);

  return (
    <div className="bg-cream min-h-screen" data-testid="admin-audit-page">
      <div className="max-w-6xl mx-auto px-6 sm:px-10 py-10 space-y-8">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="overline">Pro Shop · Activity Log</div>
            <h1 className="font-serif text-3xl text-[#1A1A1A] mt-1 flex items-center gap-3">
              <ScrollText size={28} /> Who did what
            </h1>
            <p className="text-sm text-[#4A4A4A] mt-2 max-w-2xl">
              Every back-office action in this club, stamped with the staff member who performed it
              and a timestamp. Filter by category, by staff member, or search the meta payload.
            </p>
          </div>
          <Link
            to="/admin/members"
            data-testid="admin-audit-back"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#1A422D] border border-[#1A422D]/30 px-3 py-1.5 hover:bg-[#1A422D]/5"
          >
            <ArrowLeft size={14} /> Member roster
          </Link>
        </div>

        <section data-testid="admin-audit-filters" className="surface-card p-5 space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            {ACTION_GROUPS.map((g) => (
              <button
                key={g.key}
                data-testid={`admin-audit-group-${g.key}`}
                onClick={() => setActionFilter(g.key)}
                className={`px-3 py-1.5 text-xs uppercase tracking-[0.18em] border transition-colors ${
                  actionFilter === g.key
                    ? "bg-[#1A422D] text-[#F9F8F4] border-[#1A422D]"
                    : "text-[#1A422D] border-[#1A422D]/30 hover:bg-[#1A422D]/5"
                }`}
              >
                {g.label}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="overline text-[10px] block mb-1.5">Author</label>
              <select
                data-testid="admin-audit-actor-filter"
                value={actorFilter}
                onChange={(e) => setActorFilter(e.target.value)}
                className="input-field w-full"
              >
                <option value="">Everyone</option>
                {staffOptions.map((s) => (
                  <option key={s.user_id} value={s.user_id}>
                    {s.name || s.email} · {s.role === "club_admin" ? "Shared Terminal" : (s.title || "Staff")}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="overline text-[10px] block mb-1.5">Search</label>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7A7A7A]" />
                <input
                  data-testid="admin-audit-search"
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="action, email, target id, meta…"
                  className="input-field w-full pl-9"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="surface-card overflow-hidden">
          {loading && <div className="p-6 text-sm text-[#7A7A7A]" data-testid="admin-audit-loading">Loading audit log…</div>}
          {error && (
            <div className="m-4 px-4 py-3 text-sm border border-[#A33] bg-[#fbe9e9] text-[#8a1a1a]" data-testid="admin-audit-error">
              {error}
            </div>
          )}
          {!loading && !error && visible.length === 0 && (
            <div className="p-10 text-center text-sm text-[#7A7A7A]" data-testid="admin-audit-empty">
              No audit events match these filters.
            </div>
          )}
          {!loading && visible.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-audit-table">
                <thead className="bg-[#F0EBDA] text-[#4A4A4A]">
                  <tr>
                    <Th>When</Th>
                    <Th>Who</Th>
                    <Th>Action</Th>
                    <Th>Target</Th>
                    <Th>Meta</Th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((row) => (
                    <tr
                      key={row.log_id}
                      data-testid={`admin-audit-row-${row.log_id}`}
                      className="border-t border-[#E5E3D8] hover:bg-[#FAF9F2] align-top"
                    >
                      <Td className="text-xs text-[#7A7A7A] whitespace-nowrap">{fmtDate(row.at)}</Td>
                      <Td>
                        <div className="inline-flex items-center gap-2">
                          <UserIcon size={12} className="text-[#7A7A7A]" />
                          <div>
                            <div className="text-[#1A1A1A]">{row.actor_name || row.actor_email || "—"}</div>
                            <div className="text-[10px] uppercase tracking-[0.18em] text-[#7A7A7A]">
                              {row.actor_role || "—"}
                              {row.meta?.via && <> · {row.meta.via}</>}
                            </div>
                          </div>
                        </div>
                      </Td>
                      <Td>
                        <span className="inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] border border-[#1A422D]/30 text-[#1A422D] bg-[#1A422D]/5">
                          {row.action}
                        </span>
                      </Td>
                      <Td>
                        {row.target_type && (
                          <div className="text-xs">
                            <div className="text-[#7A7A7A]">{row.target_type}</div>
                            <div className="font-mono text-[10px] text-[#1A1A1A]">{row.target_id || "—"}</div>
                          </div>
                        )}
                      </Td>
                      <Td>
                        <MetaBlock meta={row.meta} />
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function MetaBlock({ meta }) {
  if (!meta || Object.keys(meta).length === 0) return <span className="text-[#7A7A7A]">—</span>;
  // Strip noisy club_id, show a compact key=value list.
  const entries = Object.entries(meta).filter(([k]) => k !== "club_id");
  if (entries.length === 0) return <span className="text-[#7A7A7A]">—</span>;
  return (
    <div className="space-y-0.5 text-[11px] font-mono text-[#4A4A4A] max-w-[260px]">
      {entries.slice(0, 6).map(([k, v]) => (
        <div key={k} className="truncate">
          <span className="text-[#7A7A7A]">{k}=</span>
          <span className="text-[#1A1A1A]">{typeof v === "object" ? JSON.stringify(v) : String(v)}</span>
        </div>
      ))}
      {entries.length > 6 && <div className="text-[#7A7A7A]">…+{entries.length - 6} more</div>}
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`text-left px-3 py-2 overline text-[10px] ${className}`}>{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`px-3 py-3 ${className}`}>{children}</td>;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}
